import {
  ButtonCancel,
  ButtonRemove,
  classNames,
  DeleteIcon,
  DownloadIcon,
  LifeDialog,
  LifeMenu,
  ActionMenuIcon,
  AdjustmentsIcon,
  Tooltip,
  UploadIcon,
  useErrorNotification,
} from '@life/components'
import { Image, useRemoveImage } from '@life/frontend-model'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { canEditBook } from '../common'

type Props = {
  image: Image
}

export function ImageMenu({ image }: Props): JSX.Element {
  const canEdit = canEditBook(image.book)
  const [confirmDelete, setConfirmDelete] = useState(false)

  if (!canEdit) return <></>
  const storyCount = image.storiesWithImage.length

  function DeleteButton({ active }: { active: boolean }): JSX.Element {
    if (storyCount > 0)
      return (
        <Tooltip message="Remove photo from stories first">
          <button className="block w-full text-left px-4 py-2 text-sm rounded-md text-gray-300">
            Delete (disabled)
          </button>
        </Tooltip>
      )
    return (
      <button
        className={classNames(
          'flex w-full items-center text-left px-4 py-2 text-sm rounded-md',
          active ? 'bg-red-500 text-white' : 'text-gray-700'
        )}
        onClick={() => setConfirmDelete(true)}
      >
        <DeleteIcon className="mr-2 h-5 w-5" aria-hidden="true" />
        Delete from Book
      </button>
    )
  }

  return (
    <>
      <LifeMenu>
        <LifeMenu.Button className="p-1 rounded-full">
          <ActionMenuIcon className="h-5 w-5 hover:text-gray-400" aria-hidden="true" />
        </LifeMenu.Button>
        <LifeMenu.Items className="w-64">
          <LifeMenu.Item>
            {({ active }) => (
              <Link
                to="edit"
                className={classNames(
                  'flex w-full items-center text-left px-4 py-2 text-sm rounded-md',
                  active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                )}
              >
                <AdjustmentsIcon className="mr-2 h-5 w-5" aria-hidden="true" />
                Edit
              </Link>
            )}
          </LifeMenu.Item>
          <LifeMenu.Item>
            {({ active }) => (
              <Link
                to="replace"
                className={classNames(
                  'flex w-full items-center text-left px-4 py-2 text-sm rounded-md',
                  active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                )}
              >
                <UploadIcon className="mr-2 h-5 w-5" aria-hidden="true" />
                Replace
              </Link>
            )}
          </LifeMenu.Item>
          <LifeMenu.Item>
            {({ active }) => (
              <a
                href={image.originalUrl}
                target="__BLANK"
                className={classNames(
                  'flex w-full items-center text-left px-4 py-2 text-sm rounded-md',
                  active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                )}
              >
                <DownloadIcon className="mr-2 h-5 w-5" aria-hidden="true" />
                Download
              </a>
            )}
          </LifeMenu.Item>
          <LifeMenu.Item>{DeleteButton}</LifeMenu.Item>
        </LifeMenu.Items>
      </LifeMenu>
      <ConfirmDialog image={image} isOpen={confirmDelete} onClose={() => setConfirmDelete(false)} />
    </>
  )
}

type ConfirmProps = {
  image: Image
  isOpen: boolean
  onClose: VoidFunction
}
function ConfirmDialog({ image, isOpen, onClose }: ConfirmProps): JSX.Element {
  const { isRemoving, remove } = useRemoveImage()
  const { showError } = useErrorNotification()
  const navigate = useNavigate()

  async function handleRemove(): Promise<void> {
    try {
      await remove(image)
      navigate('../photos')
    } catch (error) {
      showError('Error Deleting Photo', error)
    }
  }

  const shortcuts = { Escape: onClose }
  return (
    <LifeDialog modal title="Confirmation" isOpen={isOpen} shortcuts={shortcuts} onClose={onClose}>
      <LifeDialog.Content>Are you sure you want to remove this photo from your book?</LifeDialog.Content>
      <LifeDialog.Actions>
        <ButtonCancel onClick={onClose} />
        <ButtonRemove confirm={false} clicked={isRemoving} onClick={handleRemove}>
          Delete From Book
        </ButtonRemove>
      </LifeDialog.Actions>
    </LifeDialog>
  )
}
